import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth-guard';
import { accountImportGuard } from './core/guards/account-import.guard';
import { previewDemoGuard } from './core/guards/preview-demo.guard';
import { rootRedirectGuard } from './core/guards/root-redirect.guard';
import { superAdminGuard } from './core/guards/super-admin.guard';
import { superAdminManageGuard } from './core/guards/super-admin-manage.guard';
import { manageUsersGuard } from './core/guards/manage-users.guard';
import { expiredSubscriptionAccessGuard } from './core/guards/expired-subscription-access.guard';
import { dashboardAccessGuard } from './core/guards/dashboard-access.guard';
import {
  bankManageGuard,
  bankReadGuard,
  bankReconcileGuard,
  bankReconcilePeriodGuard,
  bankReconciliationReportGuard,
  bankStatementImportGuard,
} from './core/guards/bank-access.guard';
import { reportAccessGuard, reportRunGuard } from './core/guards/report-access.guard';
import { accountReadGuard } from './core/guards/account-access.guard';
import { analysisCodeManageGuard, forecastViewGuard } from './core/guards/budgeting-access.guard';
import { manageSettingsGuard } from './core/guards/manage-settings.guard';
import { consolidatedMyInvoisApprovalGuard } from './core/guards/consolidated-myinvois-approval.guard';


export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    canActivate: [rootRedirectGuard],
    children: [],
  },
  {
    // The report pages without the app shell, framed full-screen by the Management Report so a report's layout
    // editor opens in place (pages/accounting/reports/shared/embedded-layout-editor.ts). Same guards as below.
    path: 'embed/accounting/reports',
    canActivate: [authGuard, reportAccessGuard],
    canActivateChild: [expiredSubscriptionAccessGuard],
    loadChildren: () => import('./pages/accounting/reports/reporting.routes').then(m => m.REPORTS_ROUTES),
  },
  {
    path: '',
    loadComponent: () => import('./layouts/main-layout/main-layout').then(m => m.MainLayout),
    canActivate: [authGuard],
    canActivateChild: [expiredSubscriptionAccessGuard],
    children: [
      {
        path: 'myinvois/consolidated',
        loadComponent: () => import('./pages/business/consolidated-myinvois/consolidated-myinvois').then(m => m.ConsolidatedMyInvois),
        canActivate: [authGuard, consolidatedMyInvoisApprovalGuard]
      },
      {
        path: 'my-speed',
        loadComponent: () => import('./pages/my-speed/my-speed').then(m => m.MySpeed),
        canActivate: [authGuard]
      },
      {
        path: 'dashboard',
        loadComponent: () => import('./pages/dashboard/dashboard').then(m => m.Dashboard),
        canActivate: [authGuard, dashboardAccessGuard]
      },
      {
        // SPEED-588: Sales Overview dashboard (Business ▾ → Overview). The page + navbar link
        // already existed; only the route was missing, so the menu item was a dead link.
        path: 'sales-overview',
        loadComponent: () => import('./pages/business/sales-overview/sales-overview').then(m => m.SalesOverview),
        canActivate: [authGuard]
      },
      {
        // SPEED-591: Purchase Overview dashboard (Purchases ▾ → Overview).
        path: 'purchase-overview',
        loadComponent: () => import('./pages/business/purchase-overview/purchase-overview').then(m => m.PurchaseOverview),
        canActivate: [authGuard]
      },
      {
        // SPEED-613: full "Search results for [keyword]" page (global search "Show all results").
        path: 'search',
        loadComponent: () => import('./pages/search/search-results').then(m => m.SearchResults),
        canActivate: [authGuard]
      },
      {
        path: 'chart-of-accounts',
        loadComponent: () => import('./pages/accounting/chart-of-accounts/chart-of-accounts').then(m => m.ChartOfAccounts),
        canActivate: [authGuard, accountReadGuard]
      },
      {
        path: 'chart-of-accounts/import',
        loadComponent: () => import('./pages/accounting/chart-of-accounts/import-accounts/import-accounts').then(m => m.ImportAccounts),
        canActivate: [authGuard, accountImportGuard]
      },
      {
        path: 'chart-of-accounts/import/review',
        loadComponent: () => import('./pages/accounting/chart-of-accounts/import-accounts/import-accounts-review').then(m => m.ImportAccountsReview),
        canActivate: [authGuard, accountImportGuard]
      },
      {
        path: 'conversion-balances',
        loadComponent: () => import('./pages/accounting/conversion-balances/conversion-balances').then(m => m.ConversionBalances),
        canActivate: [authGuard, accountImportGuard]
      },
      {
        // SPEED-985: the How-To guide moved to the central /help knowledge base. Keep the old URL
        // working by redirecting to its new permanent home.
        path: 'conversion-balances/help',
        redirectTo: '/help/import-conversion-balances',
        pathMatch: 'full',
      },
      {
        path: 'conversion-balances/import',
        loadComponent: () => import('./pages/accounting/conversion-balances/import-conversion-balances').then(m => m.ImportConversionBalances),
        canActivate: [authGuard, accountImportGuard]
      },
      {
        path: 'conversion-balances/import-old-system',
        loadComponent: () => import('./pages/accounting/conversion-balances/import-old-system-conversion-balances').then(m => m.ImportOldSystemConversionBalances),
        canActivate: [authGuard, accountImportGuard]
      },
      {
        path: 'conversion-balances/import-old-system/:importPublicId/sign-convention',
        loadComponent: () => import('./pages/accounting/conversion-balances/old-system-sign-convention').then(m => m.OldSystemSignConvention),
        canActivate: [authGuard, accountImportGuard]
      },
      {
        path: 'conversion-balances/manual',
        redirectTo: 'conversion-balances/review/manual',
        pathMatch: 'full'
      },
      {
        path: 'conversion-balances/review/manual',
        loadComponent: () => import('./pages/accounting/conversion-balances/conversion-balances-review').then(m => m.ConversionBalancesReview),
        canActivate: [authGuard, accountImportGuard]
      },
      {
        path: 'conversion-balances/review/:importPublicId/trial-balance',
        loadComponent: () => import('./pages/accounting/conversion-balances/conversion-balances-trial-balance').then(m => m.ConversionBalancesTrialBalance),
        canActivate: [authGuard, accountImportGuard]
      },
      {
        path: 'conversion-balances/review/:importPublicId',
        loadComponent: () => import('./pages/accounting/conversion-balances/conversion-balances-review').then(m => m.ConversionBalancesReview),
        canActivate: [authGuard, accountImportGuard]
      },
      {
        path: 'bank-accounts',
        loadComponent: () => import('./pages/accounting/bank-accounts/bank-accounts').then(m => m.BankAccounts),
        canActivate: [authGuard, bankReadGuard]
      },
      {
        // SPEED-BANK single-account workspace shell (Phase 1). The Reconcile tab is the real
        // in-shell tab; the other tabs navigate to their existing routes for now.
        path: 'bank-accounts/:bankAccountPublicId/reconcile',
        loadComponent: () => import('./pages/accounting/banking/account-workspace/account-workspace').then(m => m.AccountWorkspace),
        canActivate: [authGuard, bankReconcileGuard]
      },
      {
        // Deep-linkable Account-transactions / Bank-statements tabs of the single-account workspace
        // (SPEED-625). The Bank Accounts "..." menu's Find shortcuts land here so they open the SAME
        // in-shell tab a user reaches via Reconcile Account, instead of the GL ledger / import log.
        path: 'bank-accounts/:bankAccountPublicId/account-transactions',
        loadComponent: () => import('./pages/accounting/banking/account-workspace/account-workspace').then(m => m.AccountWorkspace),
        data: { initialTab: 'ACCOUNT_TRANSACTIONS' },
        canActivate: [authGuard, bankReconcileGuard]
      },
      {
        path: 'bank-accounts/:bankAccountPublicId/bank-statements',
        loadComponent: () => import('./pages/accounting/banking/account-workspace/account-workspace').then(m => m.AccountWorkspace),
        data: { initialTab: 'BANK_STATEMENTS' },
        canActivate: [authGuard, bankReconcileGuard]
      },

      {
        path: 'banking/statements/import',
        loadComponent: () => import('./pages/accounting/banking/import-bank-statement/import-bank-statement').then(m => m.ImportBankStatement),
        canActivate: [authGuard, bankManageGuard]
      },
      {
        path: 'banking/statements/log',
        loadComponent: () => import('./pages/accounting/banking/statement-import-log/statement-import-log').then(m => m.StatementImportLog),
        canActivate: [authGuard, bankStatementImportGuard]
      },
      {
        // Xero-parity "View Statement" detail — registered AFTER the static import/log paths so it
        // never shadows them. :publicId is a bank_statements public id.
        path: 'banking/statements/:publicId',
        loadComponent: () => import('./pages/accounting/banking/view-bank-statement/view-bank-statement').then(m => m.ViewBankStatement),
        canActivate: [authGuard, bankReconcileGuard]
      },
      {
        path: 'banking/spend-receive',
        loadComponent: () => import('./pages/accounting/banking/spend-receive-money/spend-receive-money').then(m => m.SpendReceiveMoney),
        canActivate: [authGuard, bankManageGuard]
      },
      {
        path: 'banking/transfer',
        loadComponent: () => import('./pages/accounting/banking/bank-transfer/bank-transfer').then(m => m.BankTransfer),
        canActivate: [authGuard, bankManageGuard]
      },
      {
        // The standalone split-screen reconcile workspace was replaced by the in-shell Reconcile tab
        // above (bank-accounts/:id/reconcile) — same Match/Create/Transfer/Discuss/Find & Match tabs,
        // now with server-side search/pagination. Redirect so any saved link/bookmark still lands
        // somewhere useful instead of 404ing.
        path: 'banking/reconciliation/:bankAccountPublicId',
        redirectTo: 'bank-accounts/:bankAccountPublicId/reconcile',
      },
      {
        path: 'banking/transactions/:publicId',
        loadComponent: () => import('./pages/accounting/banking/bank-transaction-detail/bank-transaction-detail').then(m => m.BankTransactionDetail),
        canActivate: [authGuard, bankReadGuard]
      },
      {
        path: 'banking/rules',
        loadComponent: () => import('./pages/accounting/banking/bank-rules/bank-rules').then(m => m.BankRules),
        canActivate: [authGuard, bankManageGuard]
      },
      {
        path: 'banking/rules/new',
        loadComponent: () => import('./pages/accounting/banking/bank-rule-builder/bank-rule-builder').then(m => m.BankRuleBuilder),
        canActivate: [authGuard, bankManageGuard]
      },
      {
        path: 'banking/rules/:publicId/edit',
        loadComponent: () => import('./pages/accounting/banking/bank-rule-builder/bank-rule-builder').then(m => m.BankRuleBuilder),
        canActivate: [authGuard, bankManageGuard]
      },
      {
        path: 'banking/credits',
        loadComponent: () => import('./pages/accounting/banking/credit-management/credit-management').then(m => m.CreditManagement),
        canActivate: [authGuard, bankManageGuard]
      },
      {
        path: 'banking/cash-coding/:bankAccountPublicId',
        loadComponent: () => import('./pages/accounting/banking/cash-coding/cash-coding').then(m => m.CashCoding),
        canActivate: [authGuard, bankReconcileGuard]
      },
      {
        // Option B (Xero parity): the deep-linkable reconcile-period URLs mount the account-workspace
        // shell with the Reconcile-period tab active, so the header + tab bar stay put instead of
        // navigating to a standalone page. The tab hosts the list <-> detail toggle in-shell.
        path: 'banking/reconcile-periods/:bankAccountPublicId',
        loadComponent: () => import('./pages/accounting/banking/account-workspace/account-workspace').then(m => m.AccountWorkspace),
        data: { initialTab: 'RECONCILE_PERIOD' },
        canActivate: [authGuard, bankReconcilePeriodGuard]
      },
      {
        path: 'banking/reconcile-periods/:bankAccountPublicId/:periodPublicId',
        loadComponent: () => import('./pages/accounting/banking/account-workspace/account-workspace').then(m => m.AccountWorkspace),
        data: { initialTab: 'RECONCILE_PERIOD' },
        canActivate: [authGuard, bankReconcilePeriodGuard]
      },
      {
        path: 'banking/reports/reconciliation/:bankAccountPublicId',
        loadComponent: () => import('./pages/accounting/banking/reconciliation-report/reconciliation-report').then(m => m.ReconciliationReport),
        canActivate: [authGuard, bankReconciliationReportGuard]
      },
      {
        path: 'banking/reports/exceptions/:bankAccountPublicId',
        loadComponent: () => import('./pages/accounting/banking/statement-exceptions/statement-exceptions').then(m => m.StatementExceptions),
        canActivate: [authGuard]
      },
      {
        path: 'chart-of-accounts/:publicId/transactions',
        loadComponent: () => import('./pages/accounting/account-transactions/account-transactions').then(m => m.AccountTransactions),
        canActivate: [authGuard, reportRunGuard]
      },
      {
        path: 'tax-rates',
        loadComponent: () => import('./pages/accounting/tax-rates/tax-rates').then(m => m.TaxRates),
        canActivate: [authGuard]
      },
      {
        path: 'manual-journals',
        loadComponent: () => import('./pages/accounting/manual-journals/manual-journals').then(m => m.ManualJournals),
        canActivate: [authGuard, manageSettingsGuard]
      },
      {
        path: 'manual-journals/import',
        loadComponent: () => import('./pages/accounting/manual-journals/import-manual-journal/import-manual-journal').then(m => m.ImportManualJournal),
        canActivate: [authGuard, manageSettingsGuard]
      },
      {
        path: 'manual-journals/import/review',
        loadComponent: () => import('./pages/accounting/manual-journals/import-manual-journal/import-manual-journal-review').then(m => m.ImportManualJournalReview),
        canActivate: [authGuard, manageSettingsGuard]
      },
      {
        path: 'manual-journals/new',
        loadComponent: () => import('./pages/accounting/manual-journals/edit-manual-journal/edit-manual-journal').then(m => m.EditManualJournal),
        canActivate: [authGuard, manageSettingsGuard]
      },
      {
        path: 'manual-journals/:publicId/edit',
        loadComponent: () => import('./pages/accounting/manual-journals/edit-manual-journal/edit-manual-journal').then(m => m.EditManualJournal),
        canActivate: [authGuard, manageSettingsGuard]
      },
      {
        path: 'manual-journals/:publicId/view',
        loadComponent: () => import('./pages/accounting/manual-journals/edit-manual-journal/edit-manual-journal').then(m => m.EditManualJournal),
        canActivate: [authGuard, manageSettingsGuard]
      },
      {
        path: 'repeating-journals/new',
        loadComponent: () => import('./pages/accounting/repeating-journals/repeating-journals').then(m => m.RepeatingJournals),
        canActivate: [authGuard, manageSettingsGuard]
      },
      {
        path: 'repeating-journals/:publicId/edit',
        loadComponent: () => import('./pages/accounting/repeating-journals/repeating-journals').then(m => m.RepeatingJournals),
        canActivate: [authGuard, manageSettingsGuard]
      },
      {
        path: 'fixed-assets',
        loadComponent: () => import('./pages/accounting/fixed-assets/asset-register/asset-register').then(m => m.AssetRegister),
        canActivate: [authGuard]
      },
      {
        path: 'fixed-assets/new',
        loadComponent: () => import('./pages/accounting/fixed-assets/asset-form/asset-form').then(m => m.AssetForm),
        canActivate: [authGuard]
      },
      {
        path: 'fixed-assets/import',
        loadComponent: () => import('./pages/accounting/fixed-assets/import-assets/import-assets').then(m => m.ImportAssets),
        canActivate: [authGuard]
      },
      {
        path: 'fixed-assets/depreciation',
        loadComponent: () => import('./pages/accounting/fixed-assets/depreciation-run/depreciation-run').then(m => m.DepreciationRun),
        canActivate: [authGuard]
      },
      {
        path: 'fixed-assets/depreciation/history',
        loadComponent: () => import('./pages/accounting/fixed-assets/depreciation-history/depreciation-history').then(m => m.DepreciationHistory),
        canActivate: [authGuard]
      },
      {
        path: 'fixed-assets/depreciation/rollback',
        loadComponent: () => import('./pages/accounting/fixed-assets/depreciation-rollback/depreciation-rollback').then(m => m.DepreciationRollback),
        canActivate: [authGuard]
      },
      {
        path: 'fixed-assets/asset-types',
        loadComponent: () => import('./pages/accounting/fixed-assets/asset-types/asset-types').then(m => m.AssetTypes),
        canActivate: [authGuard]
      },
      {
        path: 'fixed-assets/settings',
        loadComponent: () => import('./pages/accounting/fixed-assets/fixed-asset-settings/fixed-asset-settings').then(m => m.FixedAssetSettings),
        canActivate: [authGuard]
      },
      {
        path: 'fixed-assets/:publicId/edit',
        loadComponent: () => import('./pages/accounting/fixed-assets/asset-form/asset-form').then(m => m.AssetForm),
        canActivate: [authGuard]
      },
      {
        path: 'fixed-assets/:publicId',
        loadComponent: () => import('./pages/accounting/fixed-assets/asset-detail/asset-detail').then(m => m.AssetDetail),
        canActivate: [authGuard]
      },
      {
        path: 'budgeting/analysis-code-mapping',
        loadComponent: () => import('./pages/accounting/budgeting/analysis-code-mapping/analysis-code-mapping').then(m => m.AnalysisCodeMapping),
        canActivate: [authGuard, analysisCodeManageGuard]
      },
      {
        path: 'budgeting/baseline',
        loadComponent: () => import('./pages/accounting/budgeting/baseline/baseline').then(m => m.Baseline),
        canActivate: [authGuard, forecastViewGuard]
      },
      {
        path: 'budgeting/assumptions',
        loadComponent: () => import('./pages/accounting/budgeting/assumptions/assumptions').then(m => m.BudgetAssumptions),
        canActivate: [authGuard, forecastViewGuard]
      },
      {
        path: 'budgeting/versions',
        loadComponent: () => import('./pages/accounting/budgeting/budget-versions/budget-versions').then(m => m.BudgetVersions),
        canActivate: [authGuard, forecastViewGuard]
      },
      {
        path: '',
        loadChildren: () => import('./pages/business/business.routes').then(m => m.BUSINESS_ROUTES),
      },
      {
        path: 'accounting/reports',
        loadChildren: () => import('./pages/accounting/reports/reporting.routes').then(m => m.REPORTS_ROUTES),
        canActivate: [authGuard, reportAccessGuard]
      },
      {
        path: 'contacts',
        loadChildren: () => import('./pages/contact/contact.routes').then(m => m.CONTACT_ROUTES),
      },
      {
        path: 'organization/settings',
        loadChildren: () =>
          import('./pages/organization-settings/organization-settings.routes').then(m => m.ORGANIZATION_SETTINGS_ROUTES),
      },
      {
        path: 'system',
        loadComponent: () =>
          import('./pages/system/system-home/system-home').then(m => m.SystemHome),
        canActivate: [authGuard, superAdminGuard]
      },
      {
        path: 'system/ai-conversations',
        loadComponent: () =>
          import('./pages/system/ai-conversations/ai-conversations').then(m => m.AiConversations),
        canActivate: [authGuard, manageUsersGuard]
      },
      {
        path: 'system/fx-readiness',
        loadComponent: () => import('./pages/system/fx-readiness-queue/fx-readiness-queue').then(m => m.FxReadinessQueue),
        canActivate: [authGuard, superAdminManageGuard]
      },
      {
        path: 'system/billing/billplz-subscriptions',
        loadComponent: () =>
          import('./pages/system/billplz-subscription-settings/billplz-subscription-settings').then(m => m.BillplzSubscriptionSettings),
        canActivate: [authGuard, superAdminGuard]
      },
      {
        path: 'system/stripe/platform',
        loadComponent: () =>
          import('./pages/system/stripe-platform-settings/stripe-platform-settings').then(m => m.StripePlatformSettings),
        canActivate: [authGuard, superAdminGuard]
      },
      {
        path: 'profile',
        loadComponent: () => import('./pages/profile/manage-profile/manage-profile').then(m => m.ManageProfile),
        canActivate: [authGuard]
      },
      {
        // SPEED-948: SPEED How-To / Help Center — reached from the "?" icon in the header.
        // SPEED-985: each How-To article now has a clean, permanent URL directly under /help so the
        // knowledge base is independent of the feature pages it documents. The guide components still
        // live in their feature folders; only their canonical URL moved here (the old feature routes
        // 301-redirect to these). Articles are plain documentation, so they gate on authGuard only —
        // matching the /help landing that lists them — not on the source feature's permission guard.
        // SPEED-985 (review): the shell is what makes the image lightbox reach every article,
        // existing and future, without a guide importing anything. Articles are its children, so a
        // new guide is a template plus a child route here and nothing else.
        path: 'help',
        loadComponent: () => import('./pages/help/how-to-article-shell').then(m => m.HowToArticleShell),
        canActivate: [authGuard],
        children: [
          {
            path: '',
            loadComponent: () => import('./pages/help/help-center').then(m => m.HelpCenter),
          },
          {
            // SPEED-948: the middle level of the How-To — the guides inside one category. It sits
            // under `category/` rather than taking a `:slug` of its own so the five article URLs
            // below (already shared, and one of them linked from inside the app) keep resolving
            // exactly as they do today.
            path: 'category/:slug',
            loadComponent: () => import('./pages/help/help-category').then(m => m.HelpCategory),
          },
          {
            path: 'import-contacts',
            loadComponent: () => import('./pages/contact/import-contacts/import-contacts-guide').then(m => m.ImportContactsGuide),
          },
          {
            path: 'import-conversion-balances',
            loadComponent: () => import('./pages/accounting/conversion-balances/conversion-balances-help').then(m => m.ConversionBalancesHelp),
          },
          {
            path: 'set-up-lhdn-myinvois',
            loadComponent: () => import('./pages/organization-settings/integrations/lhdn-myinvois-settings/lhdn-myinvois-setup-guide').then(m => m.LhdnMyInvoisSetupGuide),
          },
          {
            // SPEED-956: "Create & Send an Invoice" (Sales & Receivable).
            path: 'create-send-invoice',
            loadComponent: () => import('./pages/business/invoices/invoice-guide').then(m => m.InvoiceGuide),
          },
          {
            // SPEED-982: "How to Upload Bills" (Purchases & Payables).
            path: 'upload-bills',
            loadComponent: () => import('./pages/business/bills/bills-upload-guide').then(m => m.BillsUploadGuide),
          },
        ],
      },
      {
        path: 'preview/demo',
        loadComponent: () => import('./pages/preview/preview-demo/preview-demo').then(m => m.PreviewDemo),
        canActivate: [authGuard, previewDemoGuard]
      },
    ]
  },
  {
    path: 'public/quotes/:publicId',
    loadComponent: () =>
      import('./pages/public/public-quote/public-quote')
        .then(m => m.PublicQuote),
  },
  {
    path: 'public/purchase-orders/:publicId',
    loadComponent: () =>
      import('./pages/public/public-purchase-order/public-purchase-order')
        .then(m => m.PublicPurchaseOrder),
  },
  {
    path: 'public/invoices/payment-result',
    loadComponent: () =>
      import('./pages/public/public-invoice-payment-result/public-invoice-payment-result')
        .then(m => m.PublicInvoicePaymentResult),
  },
  {
    path: 'public/invoices/:publicId',
    loadComponent: () =>
      import('./pages/public/public-invoice/public-invoice')
        .then(m => m.PublicInvoice),
  },
  {
    path: 'connection-request/:token',
    loadComponent: () =>
      import('./pages/public/connection-request/connection-request')
        .then(m => m.ConnectionRequest),
  },
  {
    path: 'auth',
    loadChildren: () => import('./pages/auth/auth.routes').then(m => m.default),
  },
  {
    // An unmatched URL must not masquerade as a forced logout. The old
    // `redirectTo: '/auth/login'` dumped a fully signed-in user on the login screen whenever a
    // deep link, bookmark or shared link pointed at a path this build no longer serves — which is
    // reachable only through direct navigation, since in-app links always emit a routable URL.
    // `rootRedirectGuard` (already used for the `''` route) restores the session first and then
    // sends the visitor somewhere they are allowed to be; an unauthenticated visitor still ends up
    // at /auth/login, so nothing here is less protected than before.
    path: '**',
    canActivate: [rootRedirectGuard],
    children: [],
  },
];
